import { Alert, Box } from "@mui/material";
import { useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import { Panel } from "../components/layout/Panel";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import type { Column } from "../components/grid/CompGrid";
import { createCommonLogColumns } from "../components/grid/columns/logColumns";
import { FilterBar } from "../components/filters/FilterBar";
import { BaselineSelector } from "../components/filters/BaselineSelector";
import { SeverityRangesFilter } from "../components/filters/SeverityRangesFilter";
import { RefreshControls } from "../components/filters/RefreshControls";
import { useTrace } from "../hooks/useTrace";
import { useLogs } from "../hooks/useLogs";
import type { LogRecord, Span, SpanStatisticsBaselineWindow } from "../api/client";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { useSeverityRangesFilterFromQuery } from "../hooks/useRouteParams";
import { useSpanStatistics } from "../hooks/useSpanStatistics";
import { SpanIdLink } from "../components/links/IdLink";
import { TraceSearchPanel } from "../components/trace/TraceSearchPanel";
import { TraceStatsPanel } from "../components/trace/TraceStatsPanel";
import { TraceLogsGrid } from "../components/trace/TraceLogsGrid";
import { CompLanes, type ISpan } from "./CompTraces";

export function PageTraceDetail() {
  const { traceId } = useParams<{ traceId: string }>();
  const navigate = useNavigate();
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range } = useTimeRangeFromQuery();
  const { severityRanges } = useSeverityRangesFilterFromQuery();

  const [refreshToken, setRefreshToken] = useState(0);

  const [baseline, setBaseline] = useState<SpanStatisticsBaselineWindow>("24h");

  const {
    trace,
    loading: traceLoading,
    error: traceError,
    isFetching: traceFetching,
  } = useTrace({
    serviceSlug,
    traceId: traceId ?? null,
    refreshToken,
    enabled: !!serviceSlug && !!traceId,
  });

  const {
    logs,
    loading: logsLoading,
    isFetching: logsFetching,
  } = useLogs({
    serviceSlug,
    traceIds: traceId ? [traceId] : [],
    severityRanges,
    enabled: !!serviceSlug && !!traceId,
    limit: 500,
    refreshToken,
  });

  const {
    stats: traceSpanStats,
    loading: statsLoading,
    error: statsError,
    isFetching: statsFetching,
  } = useSpanStatistics({
    serviceSlug,
    timeRange: range,
    traceIds: traceId ? [traceId] : [],
    baseline,
    enabled: !!serviceSlug && !!traceId,
    limit: 500,
    refreshToken,
  });

  const loading = traceLoading || logsLoading;
  const error = traceError;
  const isFetching = traceFetching || logsFetching || statsFetching;

  const [hoveredSpanId, setHoveredSpanId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const spans: ISpan[] = useMemo(() => {
    if (!trace) return [];

    const map = new Map<string, { source: Span, target: ISpan }>();

    for (const span of trace.spans) {
      const startDate = new Date(span.start_time);
      const endDate = new Date(span.end_time);
      const duration = endDate.getTime() - startDate.getTime();

      map.set(span.span_id, {
        source: span,
        target: {
          id: span.span_id,
          name: span.span_name,
          start: startDate,
          duration,
          children: [],
        },
      });
    }

    var roots: ISpan[] = [];

    for (const v of map.values()) {
      if (v.source.parent_span_id) {
        const parent = map.get(v.source.parent_span_id);
        if (parent) {
          parent.target.children ??= [];
          parent.target.children.push(v.target);
        } else {
          console.error(`Parent span ${v.source.parent_span_id} not found`);
        }
      } else {
        roots.push(v.target);
      }
    }

    return roots;
  }, [trace]);

  // Compute matching span IDs based on search query
  const matchingSpanIds = useMemo(() => {
    if (!trace || !searchQuery) return null;

    const query = searchQuery.toLowerCase();
    const matches: string[] = [];

    const checkSpan = (span: Span) => {
      const matchesQuery = span.span_name.toLowerCase().includes(query);

      if (matchesQuery) {
        matches.push(span.span_id);
      }
    };

    trace.spans.forEach(checkSpan);
    
    return matches;
  }, [trace, searchQuery]);

  const filteredLogs = useMemo(() => {
    if (!searchQuery || !matchingSpanIds) {
      return logs;
    }

    return logs.filter((log) => log.span_id && matchingSpanIds.includes(log.span_id));
  }, [logs, searchQuery, matchingSpanIds]);

  const handleSpanClick = useCallback(
    (spanId: string) => {
      // Single click selects; double click navigates to span detail.
      if (!serviceSlug) return;

      const params = new URLSearchParams();
      params.set("range_from", range.from.toISOString());
      params.set("range_stop", range.to.toISOString());
      navigate(
        `/services/${encodeURIComponent(serviceSlug)}/spans/${encodeURIComponent(
          spanId,
        )}?${params.toString()}`,
      );
    },
    [navigate, range.from, range.to, serviceSlug],
  );

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <PageLoadingState variant="centered" message="Loading trace…" />;
  }

  if (error || !trace) {
    return (
      <Box>
        <PageErrorState message={error || "Trace not found"} />
      </Box>
    );
  }

  const logColumns: Column<LogRecord>[] = [
    ...createCommonLogColumns({ serviceSlug }),
    {
      id: "span_id",
      label: "Span ID",
      sortable: true,
      sortValue: (log) => log.span_id || "",
      render: (log) => <SpanIdLink serviceSlug={serviceSlug} spanId={log.span_id} range={range} />,
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <TraceSearchPanel
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            matchingCount={matchingSpanIds?.length}
          />
          <SeverityRangesFilter />
          <BaselineSelector value={baseline} onChange={setBaseline} />
          <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
        </FilterBar>
      </Panel>

      <Panel title="Spans (new)">
        <CompLanes 
          spans={spans} 
          autoExpand={3}
          filterSpanIds={matchingSpanIds}
          onClick={(span) => { handleSpanClick(span.id); }}
          onHover={(span, active) => { setHoveredSpanId(active ? span.id : null); }}
          />
      </Panel>

      <TraceStatsPanel
        baseline={baseline}
        serviceSlug={serviceSlug}
        traceId={traceId ?? null}
        stats={traceSpanStats}
        spans={trace?.spans}
        loading={statsLoading}
        error={statsError}
      />

      <TraceLogsGrid
        logs={filteredLogs}
        loading={loading}
        error={error}
        columns={logColumns}
        hoveredSpanId={hoveredSpanId}
      />
    </Box>
  );
}
